// Rows-per-page options offered on paginated list views. Keep in sync with the
// PHP `perPageOptions()` / default in app/helpers.php (the validation authority).
export const PER_PAGE_OPTIONS = [10, 20, 50, 100]
export const DEFAULT_PER_PAGE = 20

// Every partner status except "archived". Used to build deep links that should
// exclude archived partners (an empty status filter means "Any", incl.
// archived). Keep in sync with the status select_options in
// app/Services/App/Views/Program/Partners/TableConfig.php (the authority).
export const NON_ARCHIVED_PARTNER_STATUSES = [
    'pending',
    'submitted',
    'approved',
    'rejected',
    'suspended',
    'invited',
]

/**
 * Drops the page number from a list url, so a changed page size or sort order
 * starts from the first page instead of landing on a page that no longer holds
 * the same rows.
 */
export const stripPageParam = (url) => {
    if (!url) {
        return url
    }

    try {
        const parsed = new URL(url, window.location.origin)
        parsed.searchParams.delete('page')
        return parsed.toString()
    } catch (e) {
        return url
    }
}

export const isCustomColor = (setting = null) => {
    return String(setting).trim().startsWith('#')
}

/**
 * Whether a hex color is "dark" (low perceived brightness). Used to decide when
 * dashboard surfaces/inputs need dark-mode contrast overrides. Returns false for
 * non-hex values so named Tailwind colors never trigger dark overrides.
 */
export const isDarkColor = (color = null) => {
    if (!isCustomColor(color)) {
        return false;
    }
    const rgb = hexToRgbString(color).split(',').map(v => parseInt(v.trim(), 10));
    if (rgb.length !== 3 || rgb.some(isNaN)) {
        return false;
    }
    const [r, g, b] = rgb;
    // Perceived brightness (ITU-R BT.601). < 140 reads as a dark surface.
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    return brightness < 140;
}

export const isNumber = (value) => {
    return typeof value === 'number'
}

export const createRouteForTemplate = (path, params = {}) => {
    return route(path, params)
}

export const generateRandomShortString = () => {
    return (Math.random() + 1).toString(36).substring(7);
}

export const setFilterFields = (filters, filterList, setToDefaults = false) => {
    Object.keys(filterList).forEach(filterField => setInitialFilterFieldValue(filters, filterList, filterField, setToDefaults))
}

export const isRoute = (routeName) => {
    return route().current() === routeName;
};

export const basename = (path) => {
    return path.split('/').filter(Boolean).pop();
}

export const scrollToTop = () => {
    window.scrollTo({
        top: 0,
        behavior: 'smooth'
    });
}

function setInitialFilterFieldValue(filters, filterList, filterField, setToDefaults) {
    switch (filterList[filterField].type) {
        case 'select':
            /*
            *  request_value is value when redirecting from some other page - check Program/Partners/FilterableFields.php
            *  setToDefaults means don't use requested values (needed when clearing all filters from Clear all button)
            */
            if (setToDefaults) {
                filters[filterField] = filterList[filterField].default_value || ''
            } else {
                filters[filterField] = filterList[filterField].request_value || filterList[filterField].default_value || ''
            }
            break
        case 'text_with_condition_select':
            filters[filterField] = filterList[filterField].default_text || ''
            filters[filterField + 'Condition'] = filterList[filterField].default_select_value || ''
            break
        case 'range_datepicker':
            filters.date_from = filterList[filterField].date_from
            filters.date_to = filterList[filterField].date_to
            break
        case 'date_range':
            // Analytics-style calendar. Stored as date_from/date_to (YYYY-MM-DD);
            // empty means "All time". The field key itself (e.g. `date`) holds no
            // value here.
            if (setToDefaults) {
                filters.date_from = ''
                filters.date_to = ''
            } else {
                filters.date_from = filterList[filterField].request_value_from || ''
                filters.date_to = filterList[filterField].request_value_to || ''
            }
            break
        case 'data_transfer':
            filters[filterField] = filterList[filterField].value;
            break
        case 'partner_search':
            if (setToDefaults) {
                filters[filterField] = filterList[filterField].default_value || ''
            } else {
                filters[filterField] = filterList[filterField].request_value || filterList[filterField].default_value || ''
            }
            break
        case 'multi_select': {
            const defaultValue = Array.isArray(filterList[filterField].default_value)
                ? [...filterList[filterField].default_value]
                : []
            if (setToDefaults) {
                filters[filterField] = defaultValue
            } else {
                const requestValue = filterList[filterField].request_value
                if (Array.isArray(requestValue)) {
                    filters[filterField] = [...requestValue]
                } else if (typeof requestValue === 'string' && requestValue !== '') {
                    filters[filterField] = requestValue.split(',').map(v => v.trim()).filter(Boolean)
                } else {
                    filters[filterField] = defaultValue
                }
            }
            break
        }
    }
    return filterList[filterField];
}

export const formatNumber = (number) => {
    if(!isNaN(Number(number))) {
        return Number(number).toLocaleString('us');
    }
    return number;
}

export const formatCurrency = (value, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {style: 'currency', currency, minimumFractionDigits: 0, maximumFractionDigits: 0}).format(value || 0);
}

export const copyToClipboard = (text) => {
    if (!navigator.clipboard) {
        return fallbackCopyToClipboard(text);
    }
    else {
        navigator.clipboard
        .writeText(text)
        .then(
            () => { return true }, // success callback
            (err) => { return fallbackCopyToClipboard(text) } // failed callback
        );
    }
};

function fallbackCopyToClipboard (text) {
    let isCopied = false;
    let textArea = document.createElement("textarea");
    textArea.value = text;

    // Avoid scrolling to bottom
    textArea.style.top = "0";
    textArea.style.left = "0";
    textArea.style.position = "fixed";

    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
        document.execCommand('copy');
        isCopied = true;
    }
    catch (err) {
    }

    document.body.removeChild(textArea);
    return isCopied;
}


export const rightTrimSlashes = (str) => {
    return str.replace(/\/+$/g, '');
}

export const leftTrimSlashes = (str) => {
    return str.replace(/^\/+/g, '');
}

export const trimSlashes = (str) => {
    return rightTrimSlashes(leftTrimSlashes(str))
}

export const ellipsisString = (string, maxLength) => {
    if(string.length > maxLength) {
        return string.substring(0, maxLength) + '...';
    }
    return string;
}

export const camelCase = (str) => {
    return str
        .replace(/[^a-zA-Z0-9 ]/g, '')
        .replace(/(?:^\w|[A-Z]|\b\w)/g, (word, index) =>
            index === 0 ? word.toLowerCase() : word.toUpperCase()
        )
        .replace(/\s+/g, '');
};

export const brandNamesMapper = {
    stripe: 'Stripe',
    paddle: 'Paddle',
    paypal: 'PayPal',
    shopify: 'Shopify',
    woocommerce: 'WooCommerce',
    chargebee: 'Chargebee',
}

export function injectStyleBlock(cssString, id = 'dynamic-style-block') {
    let styleElement = document.getElementById(id);

    if (styleElement) {
        styleElement.textContent = cssString;
    } else {
        // Create new style element
        styleElement = document.createElement('style');
        styleElement.type = 'text/css';
        styleElement.id = id;
        styleElement.textContent = cssString;
        document.head.appendChild(styleElement);
    }
}

export function hexToRgbString(hex) {
    hex = hex.replace('#', '');

    if (hex.length === 3) {
        hex = hex.split('').map(c => c + c).join('');
    }

    if (hex.length !== 6) {
        return '0,0,0'; // fallback
    }

    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    return `${r}, ${g}, ${b}`;
}

export function normalizeObjectByLowercaseKeys(obj) {
    const normalized = {};
    if (!obj || typeof obj !== 'object') {
        return normalized;
    }
    for (const [key, value] of Object.entries(obj)) {
        normalized[key.toLowerCase()] = value;
    }
    return normalized;
}

export function getByKey(normalizedObj, keyToFind) {
    if (!normalizedObj || typeof normalizedObj !== 'object') {
        return null;
    }
    const key = keyToFind?.toLowerCase();
    return key && key in normalizedObj
        ? normalizedObj[key]
        : Object.values(normalizedObj)[0] ?? null;
}
